import { cn, Text } from '@risksmart-app/atomic-ui';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { formatScheduleDay } from '@/utils/scheduler';

import {
  buildOccurrencePreview,
  PREVIEW_OCCURRENCE_COUNT,
} from './occurrencePreview';
import { useRecurrenceValues } from './useRecurrenceValues';

export const ScheduleOccurrences: FC = () => {
  const { t } = useTranslation(['common'], { keyPrefix: 'scheduler.preview' });
  const values = useRecurrenceValues();
  const preview = buildOccurrencePreview(values);

  return (
    <div className={cn('flex flex-col gap-2')}>
      {/*
        `body-md` throughout this section: the form-field labels and control text
        above it are all `text-base`, and `Text` defaults to the smaller
        `body-sm` — including the inner spans below, which take no preset of
        their own.
      */}
      <Text preset={'body-md'} className={cn('font-bold')}>
        {t('heading', { total: PREVIEW_OCCURRENCE_COUNT })}
      </Text>
      {preview.status === 'ready' ? (
        // `pl-0` because the browser's list indent would inset the rows from
        // every other field in the section.
        <ol className={cn('flex flex-col gap-2 list-none pl-0')}>
          {preview.windows.map((window, index) => (
            <li
              key={window.WindowDue.toISOString()}
              className={cn(
                'flex items-center justify-between gap-4 rounded-lg bg-neutral-muted-minimal px-4 py-3'
              )}
            >
              {/*
                The spaces are inside the expressions rather than between the
                elements: JSX collapses whitespace around newlines, so relying on
                it would make the rendered sentence depend on how this formats.
              */}
              <Text preset={'body-md'}>
                <Text render={<span />} preset={'body-md'} variant={'muted'}>
                  {`${t('starts_label')} `}
                </Text>
                <Text
                  render={<span />}
                  preset={'body-md'}
                  className={cn('font-medium')}
                >
                  {formatScheduleDay(window.WindowStart)}
                </Text>
                <Text render={<span />} preset={'body-md'} variant={'muted'}>
                  {` → ${t('due_label')} `}
                </Text>
                <Text
                  render={<span />}
                  preset={'body-md'}
                  className={cn('font-medium')}
                >
                  {formatScheduleDay(window.WindowDue)}
                </Text>
              </Text>
              <Text preset={'body-md'} variant={'muted'}>
                {index === 0 ? t('first') : t('nth', { index: index + 1 })}
              </Text>
            </li>
          ))}
        </ol>
      ) : (
        <Text preset={'body-md'} variant={'muted'}>
          {t(preview.status === 'incomplete' ? 'incomplete' : 'unavailable')}
        </Text>
      )}
    </div>
  );
};
